// QuestionDOBAgeSelector.js
import React from "react";
import {
  QuestionDropdownModel,
  QuestionFactory,
  Serializer,
  ItemValue,
} from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "dobAgeSelector";
let isRegistered = false;

class QuestionDOBAgeSelectorModel extends QuestionDropdownModel {
  constructor(name) {
    super(name);

    // ✅ Set default properties
    this.placeholder = "Select Age";
    this.description = "Select age from 1 to 100 years";

    // ✅ Initialize choices with fixed 1-100 range IMMEDIATELY
    this.setFixedChoices();
  }

  getType() {
    return QUESTION_TYPE;
  }

  // ✅ Set fixed choices from 1 to 100
  setFixedChoices() {
    const fixedChoices = [];
    for (let i = 1; i <= 100; i++) {
      const suffix = this.getAgeSuffix(i);
      fixedChoices.push(new ItemValue(i, `${i} ${suffix}`));
    }
    // ✅ Directly set the property value to avoid infinite loops
    this.setPropertyValue("choices", fixedChoices);
  }

  // ✅ Get age suffix (year/years)
  getAgeSuffix(age) {
    return age === 1 ? "year" : "years";
  }

  // ✅ Override to prevent modifying choices but allow initial set
  set choices(val) {
    // Only allow setting if it's the initial setup or from Survey Creator
    if (!this.getPropertyValue("choices")) {
      this.setPropertyValue("choices", val);
    } else {
      console.warn(
        "DOB Age Selector choices are fixed (1-100) and cannot be modified."
      );
      // Keep the fixed 1-100 choices
      this.setFixedChoices();
    }
  }

  get choices() {
    // ✅ Always return the fixed choices
    let choices = this.getPropertyValue("choices");
    if (!choices || choices.length === 0) {
      this.setFixedChoices();
      choices = this.getPropertyValue("choices");
    }
    return choices;
  }

  // ✅ Override to ensure choices are loaded in preview
  get visibleChoices() {
    const choices = this.choices;
    if (!choices || choices.length === 0) {
      this.setFixedChoices();
      return this.getPropertyValue("choices") || [];
    }
    return choices;
  }

  get placeholder() {
    return this.getPropertyValue("placeholder", "Select Age");
  }

  set placeholder(val) {
    this.setPropertyValue("placeholder", val);
  }

  get description() {
    return this.getPropertyValue(
      "description",
      "Select age from 1 to 100 years"
    );
  }

  set description(val) {
    this.setPropertyValue("description", val);
  }
}

class SurveyQuestionDOBAgeSelector extends SurveyQuestionElementBase {
  constructor(props) {
    super(props);

    // ✅ Ensure choices are initialized when component mounts
    if (props.question) {
      props.question.setFixedChoices();
    }
  }

  get question() {
    return this.props.question;
  }

  componentDidMount() {
    // ✅ Double-check choices are set when component mounts
    if (
      this.question &&
      (!this.question.choices || this.question.choices.length === 0)
    ) {
      this.question.setFixedChoices();
    }
  }

  handleChange = (e) => {
    const selectedValue = e.target.value || null;
    this.question.value = selectedValue;
  };

  renderElement() {
    const q = this.question;
    const value = q.value ?? "";

    // ✅ Ensure choices are loaded
    const choices = q.visibleChoices || q.choices || [];

    return (
      <div style={{ position: "relative" }}>
        {/* Description (optional) */}
        {q.description && (
          <div
            style={{
              fontSize: "14px",
              color: "#6c757d",
              marginBottom: "8px",
              fontStyle: "italic",
            }}
          >
            {q.description}
          </div>
        )}

        <select
          value={value}
          onChange={this.handleChange}
          disabled={q.isReadOnly}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "8px 12px",
            paddingRight: "40px",
            borderRadius: "4px",
            border:
              q.errors.length > 0 ? "1px solid #dc3545" : "1px solid #ced4da",
            backgroundColor: q.isReadOnly ? "#e9ecef" : "#fff",
            fontSize: "16px",
            height: "40px",
            appearance: "menulist",
            cursor: "pointer",
          }}
          title="Select age from 1 to 100 years"
        >
          <option value="">{q.placeholder}</option>
          {choices.map((choice, index) => (
            <option
              key={choice.value || index}
              value={choice.value}
              title={`${choice.text}`}
            >
              {choice.text}
            </option>
          ))}
        </select>

        {/* Selected age display */}
        {value && (
          <div
            style={{
              marginTop: "8px",
              padding: "8px",
              backgroundColor: "#e7f3ff",
              borderRadius: "4px",
              borderLeft: "4px solid #007bff",
              fontSize: "14px",
            }}
          >
            <strong>Selected Age:</strong> {value}{" "}
            {parseInt(value) === 1 ? "year" : "years"}
          </div>
        )}
      </div>
    );
  }
}

export function registerDOBAgeSelectorQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  Serializer.addClass(
    QUESTION_TYPE,
    [
      {
        name: "placeholder:text",
        category: "general",
        default: "Select Age",
        visible: true,
      },
      {
        name: "description:text",
        category: "general",
        default: "Select age from 1 to 100 years",
        visible: true,
      },
      {
        name: "name:text",
        category: "general",
        visible: true,
        // ✅ Force text input type
        onSetValue: function (obj, value) {
          obj.name = value;
        },
      },
      {
        name: "title",
        category: "general",
        visible: true,
      },
      {
        name: "isRequired:boolean",
        category: "general",
        visible: true,
      },
      {
        name: "readOnly:boolean",
        category: "general",
        visible: true,
      },
      // ✅ INTENTIONALLY NOT including "choices" property
    ],
    () => new QuestionDOBAgeSelectorModel(""),
    "dropdown"
  );

  // ✅ Remove choices property completely for this question type
  const choicesProperty = Serializer.findProperty("dropdown", "choices");
  if (choicesProperty) {
    // Create a completely hidden, read-only choices property
    Serializer.addProperty(QUESTION_TYPE, {
      name: "choices:itemvalues",
      category: "general",
      visible: false, // ✅ HIDE from property grid
      readOnly: true, // ✅ Make read-only
      isSerializable: false, // ✅ Don't serialize to JSON
      onSetValue: function (obj, value) {
        // ✅ Ignore any attempts to set choices
        console.log("Choices are fixed for DOB Age Selector");
      },
      onGetValue: function (obj) {
        // ✅ Return empty array to prevent editing
        return [];
      },
    });
  }

  // ✅ Register with QuestionFactory
  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionDOBAgeSelectorModel(name)
  );

  // ✅ Register React component
  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) =>
    React.createElement(SurveyQuestionDOBAgeSelector, props)
  );
}

// Auto-register
registerDOBAgeSelectorQuestion();

export default QuestionDOBAgeSelectorModel;
